import { useCallback, useEffect, useState } from 'react';
import {
  createBasicAuthorization,
  fetchAdminGameMenuConfig,
  fetchAdminHiddenPointsConfig,
  fetchAdminLocationGpsConfig,
  fetchAdminMailConfig,
  resetAdminGameMenuConfig,
  resetAdminHiddenPointsConfig,
  resetAdminLocationGpsConfig,
  resetAdminMailConfig,
  saveAdminGameMenuConfig,
  saveAdminHiddenPointsConfig,
  saveAdminLocationGpsConfig,
  saveAdminMailConfig,
} from './infrastructure/adminApi';
import AdminLogin from './presentation/AdminLogin';
import EventSubscribersPanel from './presentation/EventSubscribersPanel';
import RankingAdminPanel from './presentation/RankingAdminPanel';
import ClosureInvitationsPanel from './presentation/ClosureInvitationsPanel';
import TournamentAdminPanel from './presentation/TournamentAdminPanel';
import GameMenuEditor from './presentation/GameMenuEditor';
import HiddenPointsEditor from './presentation/HiddenPointsEditor';
import HiddenPointsHoneypotPanel from './presentation/HiddenPointsHoneypotPanel';
import HiddenPointsSignalsPanel from './presentation/HiddenPointsSignalsPanel';
import LocationGpsEditor from './presentation/LocationGpsEditor';
import SmtpEditor from './presentation/SmtpEditor';
import nacanacaLogo from '../../assets/logo.webp';

const SESSION_KEY = 'nacanaca-admin-authorization';

export default function AdminApp() {
  const [authorization, setAuthorization] = useState(() => sessionStorage.getItem(SESSION_KEY) || '');
  const [menuResource, setMenuResource] = useState(null);
  const [hiddenPointsResource, setHiddenPointsResource] = useState(null);
  const [locationGpsResource, setLocationGpsResource] = useState(null);
  const [mailResource, setMailResource] = useState(null);
  const [activeTab, setActiveTab] = useState('menu');
  const [isLoading, setIsLoading] = useState(Boolean(authorization));
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const logout = useCallback((message = '') => {
    sessionStorage.removeItem(SESSION_KEY);
    setAuthorization('');
    setMenuResource(null);
    setHiddenPointsResource(null);
    setLocationGpsResource(null);
    setMailResource(null);
    setError(message);
    setNotice('');
  }, []);

  const loadAll = useCallback(async (auth) => {
    const [menu, hiddenPoints, locationGps, mail] = await Promise.all([
      fetchAdminGameMenuConfig(auth),
      fetchAdminHiddenPointsConfig(auth),
      fetchAdminLocationGpsConfig(auth),
      fetchAdminMailConfig(auth),
    ]);
    setMenuResource(menu);
    setHiddenPointsResource(hiddenPoints);
    setLocationGpsResource(locationGps);
    setMailResource(mail);
  }, []);

  useEffect(() => {
    if (!authorization || (menuResource && mailResource)) return;
    setIsLoading(true);
    loadAll(authorization)
      .then(() => setError(''))
      .catch((requestError) => logout(requestError.status === 401 ? 'La sesión expiró o las credenciales cambiaron.' : requestError.message))
      .finally(() => setIsLoading(false));
  }, [authorization, logout, loadAll, menuResource, mailResource]);

  const login = async (username, password) => {
    setIsLoading(true);
    setError('');
    const nextAuthorization = createBasicAuthorization(username, password);
    try {
      const [menu] = await Promise.all([
        fetchAdminGameMenuConfig(nextAuthorization),
        fetchAdminMailConfig(nextAuthorization).catch(() => null),
      ]);
      sessionStorage.setItem(SESSION_KEY, nextAuthorization);
      setAuthorization(nextAuthorization);
      setMenuResource(menu);
      // Carga puntos ocultos, GPS y correo en segundo plano para no bloquear.
      fetchAdminHiddenPointsConfig(nextAuthorization)
        .then(setHiddenPointsResource)
        .catch(() => {});
      fetchAdminLocationGpsConfig(nextAuthorization)
        .then(setLocationGpsResource)
        .catch(() => {});
      fetchAdminMailConfig(nextAuthorization)
        .then(setMailResource)
        .catch(() => {});
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setIsLoading(false);
    }
  };

  const saveMenu = async (config) => {
    setIsSaving(true);
    setError('');
    setNotice('');
    try {
      const payload = await saveAdminGameMenuConfig(authorization, config);
      setMenuResource(payload);
      setNotice('Cambios guardados. La próxima carga del juego usará esta versión.');
    } catch (requestError) {
      if (requestError.status === 401) logout('La sesión expiró o las credenciales cambiaron.');
      else setError(requestError.message);
    } finally {
      setIsSaving(false);
    }
  };

  const resetMenu = async () => {
    if (!window.confirm('¿Restaurar la configuración predeterminada del menú?')) return;
    setIsSaving(true);
    setError('');
    setNotice('');
    try {
      const payload = await resetAdminGameMenuConfig(authorization);
      setMenuResource(payload);
      setNotice('Se restauraron los valores predeterminados.');
    } catch (requestError) {
      if (requestError.status === 401) logout('La sesión expiró o las credenciales cambiaron.');
      else setError(requestError.message);
    } finally {
      setIsSaving(false);
    }
  };

  const saveHiddenPoints = async (config) => {
    setIsSaving(true);
    setError('');
    setNotice('');
    try {
      const payload = await saveAdminHiddenPointsConfig(authorization, config);
      setHiddenPointsResource(payload);
      setNotice('Puntos ocultos guardados. Los próximos hallazgos usarán este valor.');
    } catch (requestError) {
      if (requestError.status === 401) logout('La sesión expiró o las credenciales cambiaron.');
      else setError(requestError.message);
    } finally {
      setIsSaving(false);
    }
  };

  const resetHiddenPoints = async () => {
    if (!window.confirm('¿Restaurar los valores predeterminados de puntos ocultos (10 puntos)?')) return;
    setIsSaving(true);
    setError('');
    setNotice('');
    try {
      const payload = await resetAdminHiddenPointsConfig(authorization);
      setHiddenPointsResource(payload);
      setNotice('Se restauraron los valores predeterminados.');
    } catch (requestError) {
      if (requestError.status === 401) logout('La sesión expiró o las credenciales cambiaron.');
      else setError(requestError.message);
    } finally {
      setIsSaving(false);
    }
  };

  const saveLocationGps = async (config) => {
    setIsSaving(true);
    setError('');
    setNotice('');
    try {
      const payload = await saveAdminLocationGpsConfig(authorization, config);
      setLocationGpsResource(payload);
      setNotice('Ubicación GPS guardada. Las próximas detecciones usarán estas coordenadas y radios.');
    } catch (requestError) {
      if (requestError.status === 401) logout('La sesión expiró o las credenciales cambiaron.');
      else setError(requestError.message);
    } finally {
      setIsSaving(false);
    }
  };

  const resetLocationGps = async () => {
    if (!window.confirm('¿Restaurar las coordenadas predeterminadas de Toluca?')) return;
    setIsSaving(true);
    setError('');
    setNotice('');
    try {
      const payload = await resetAdminLocationGpsConfig(authorization);
      setLocationGpsResource(payload);
      setNotice('Se restauraron los valores predeterminados.');
    } catch (requestError) {
      if (requestError.status === 401) logout('La sesión expiró o las credenciales cambiaron.');
      else setError(requestError.message);
    } finally {
      setIsSaving(false);
    }
  };

  const saveMail = async (config) => {
    setIsSaving(true);
    setError('');
    setNotice('');
    try {
      const payload = await saveAdminMailConfig(authorization, config);
      setMailResource(payload);
      setNotice('Correo guardado. Usa “Probar envío” para verificar que llegan los mensajes.');
    } catch (requestError) {
      if (requestError.status === 401) logout('La sesión expiró o las credenciales cambiaron.');
      else setError(requestError.message);
    } finally {
      setIsSaving(false);
    }
  };

  const resetMail = async () => {
    if (!window.confirm('¿Borrar la configuración SMTP? Los jugadores no podrán recibir códigos.')) return;
    setIsSaving(true);
    setError('');
    setNotice('');
    try {
      const payload = await resetAdminMailConfig(authorization);
      setMailResource(payload);
      setNotice('Se borró la configuración de correo.');
    } catch (requestError) {
      if (requestError.status === 401) logout('La sesión expiró o las credenciales cambiaron.');
      else setError(requestError.message);
    } finally {
      setIsSaving(false);
    }
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    setError('');
    setNotice('');
  };

  if (!authorization || !menuResource) return <AdminLogin onLogin={login} error={error} isLoading={isLoading} />;

  return (
    <main className="admin-grid min-h-screen bg-[#09070d] text-[#eee8f5]">
      <header className="border-b border-[#312a3a] bg-[#0d0b11]/95 px-5 py-4 backdrop-blur sm:px-8">
        <div className="mx-auto flex max-w-[1480px] items-center justify-between gap-5">
          <div className="flex min-w-0 items-center gap-4">
            <img src={nacanacaLogo} alt="Ñaca Ñaca" className="w-20 sm:w-24" />
            <div className="border-l border-[#4e405b] pl-4">
              <p className="font-mono text-[9px] uppercase tracking-[.2em] text-[#9d8cab]">Consola del ritual</p>
                <h1 className="truncate font-display text-2xl uppercase text-white sm:text-3xl">
                  {activeTab === 'menu' ? 'Pantalla principal' : activeTab === 'hidden-points' ? 'Puntos ocultos' : activeTab === 'location-gps' ? 'Ubicación GPS' : activeTab === 'events' ? 'Eventos en Toluca' : activeTab === 'ranking' ? 'Puntajes y ranking' : activeTab === 'tournaments' ? 'Torneos globales' : 'Correo y accesos'}
                </h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/juego?modo=solo" target="_blank" rel="noreferrer" className="hidden border border-[#594a65] px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-[#d6c9df] sm:block">Abrir juego</a>
            <button type="button" onClick={() => logout()} className="px-2 py-2 text-[10px] font-bold uppercase tracking-wider text-[#a99ab4] underline underline-offset-4">Salir</button>
          </div>
        </div>
        <nav className="mx-auto mt-4 flex max-w-[1480px] gap-2" aria-label="Secciones de administración">
          <button
            type="button"
            onClick={() => switchTab('menu')}
            aria-current={activeTab === 'menu' ? 'page' : undefined}
            className={`border px-4 py-2.5 text-[10px] font-black uppercase tracking-[.14em] transition ${activeTab === 'menu' ? 'border-[#d8b6ee] bg-[#754196] text-white' : 'border-[#3a3344] text-[#a99ab4] hover:text-white'}`}
          >
            Menú del juego
          </button>
          <button
            type="button"
            onClick={() => switchTab('hidden-points')}
            aria-current={activeTab === 'hidden-points' ? 'page' : undefined}
            className={`border px-4 py-2.5 text-[10px] font-black uppercase tracking-[.14em] transition ${activeTab === 'hidden-points' ? 'border-[#d8b6ee] bg-[#754196] text-white' : 'border-[#3a3344] text-[#a99ab4] hover:text-white'}`}
          >
            Puntos ocultos
          </button>
          <button
            type="button"
            onClick={() => switchTab('location-gps')}
            aria-current={activeTab === 'location-gps' ? 'page' : undefined}
            className={`border px-4 py-2.5 text-[10px] font-black uppercase tracking-[.14em] transition ${activeTab === 'location-gps' ? 'border-[#d8b6ee] bg-[#754196] text-white' : 'border-[#3a3344] text-[#a99ab4] hover:text-white'}`}
          >
            Ubicación GPS
          </button>
          <button
            type="button"
            onClick={() => switchTab('mail')}
            aria-current={activeTab === 'mail' ? 'page' : undefined}
            className={`border px-4 py-2.5 text-[10px] font-black uppercase tracking-[.14em] transition ${activeTab === 'mail' ? 'border-[#d8b6ee] bg-[#754196] text-white' : 'border-[#3a3344] text-[#a99ab4] hover:text-white'}`}
          >
            Correo SMTP
            {mailResource && !mailResource.config.host && <span className="ml-2 rounded-sm bg-[#F7E04B] px-1.5 py-0.5 text-[9px] text-black">Sin configurar</span>}
          </button>
          <button
            type="button"
            onClick={() => switchTab('events')}
            aria-current={activeTab === 'events' ? 'page' : undefined}
            className={`border px-4 py-2.5 text-[10px] font-black uppercase tracking-[.14em] transition ${activeTab === 'events' ? 'border-[#d8b6ee] bg-[#754196] text-white' : 'border-[#3a3344] text-[#a99ab4] hover:text-white'}`}
          >
            Eventos Toluca
          </button>
          <button
            type="button"
            onClick={() => switchTab('ranking')}
            aria-current={activeTab === 'ranking' ? 'page' : undefined}
            className={`border px-4 py-2.5 text-[10px] font-black uppercase tracking-[.14em] transition ${activeTab === 'ranking' ? 'border-[#d8b6ee] bg-[#754196] text-white' : 'border-[#3a3344] text-[#a99ab4] hover:text-white'}`}
          >
            Puntajes
          </button>
          <button
            type="button"
            onClick={() => switchTab('tournaments')}
            aria-current={activeTab === 'tournaments' ? 'page' : undefined}
            className={`border px-4 py-2.5 text-[10px] font-black uppercase tracking-[.14em] transition ${activeTab === 'tournaments' ? 'border-[#d8b6ee] bg-[#754196] text-white' : 'border-[#3a3344] text-[#a99ab4] hover:text-white'}`}
          >
            Torneos
          </button>
        </nav>
      </header>

      {activeTab === 'menu' ? (
        <GameMenuEditor
          embedded
          resource={menuResource}
          onSave={saveMenu}
          onReset={resetMenu}
          onLogout={() => logout()}
          isSaving={isSaving}
          error={error}
          notice={notice}
        />
      ) : activeTab === 'hidden-points' ? (
        !hiddenPointsResource ? (
          <p className="mx-auto max-w-[1480px] px-5 py-10 font-mono text-xs text-[#8f829c] lg:px-8">Cargando puntos ocultos…</p>
        ) : (
          <>
            <HiddenPointsEditor
              resource={hiddenPointsResource}
              onSave={saveHiddenPoints}
              onReset={resetHiddenPoints}
              isSaving={isSaving}
              error={error}
              notice={notice}
            />
            <HiddenPointsHoneypotPanel authorization={authorization} onSessionExpired={logout} />
            <HiddenPointsSignalsPanel authorization={authorization} onSessionExpired={logout} />
          </>
        )
      ) : activeTab === 'location-gps' ? (
        !locationGpsResource ? (
          <p className="mx-auto max-w-[1480px] px-5 py-10 font-mono text-xs text-[#8f829c] lg:px-8">Cargando ubicación GPS…</p>
        ) : (
          <LocationGpsEditor
            resource={locationGpsResource}
            onSave={saveLocationGps}
            onReset={resetLocationGps}
            isSaving={isSaving}
            error={error}
            notice={notice}
          />
        )
      ) : activeTab === 'events' ? (
        <EventSubscribersPanel
          authorization={authorization}
          onSessionExpired={() => logout('La sesión expiró o las credenciales cambiaron.')}
        />
      ) : activeTab === 'ranking' ? (
        <>
          <RankingAdminPanel
            authorization={authorization}
            onSessionExpired={() => logout('La sesión expiró o las credenciales cambiaron.')}
          />
          <ClosureInvitationsPanel
            authorization={authorization}
            onSessionExpired={() => logout('La sesión expiró o las credenciales cambiaron.')}
          />
        </>
      ) : activeTab === 'tournaments' ? (
        <TournamentAdminPanel
          authorization={authorization}
          onSessionExpired={() => logout('La sesión expiró o las credenciales cambiaron.')}
        />
      ) : !mailResource ? (
        <p className="mx-auto max-w-[1480px] px-5 py-10 font-mono text-xs text-[#8f829c] lg:px-8">Cargando configuración de correo…</p>
      ) : (
        <SmtpEditor
          authorization={authorization}
          resource={mailResource}
          onSave={saveMail}
          onReset={resetMail}
          isSaving={isSaving}
          error={error}
          notice={notice}
        />
      )}
    </main>
  );
}
